import { useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TOOLS, STAGE_LABELS } from "../data/tools.js";
import { TRAM } from "../data/lo-trinh.js";
import { normalize } from "../lib/workspace.js";
import { CAU_HOI_CONG_CU } from "../data/toolCauHoi.js";
import CongCuIcon from "../components/CongCuIcon.jsx";
import CongCuBanDo from "../components/CongCuBanDo.jsx";
import { MAU_TRAM } from "../data/mauTram.js";
import { docChon, ghiChon, locVaDem } from "@shared/locMuc.js";
import { KhungHaiCot, NhomLoc, OTim, DongDem } from "../components/LocHaiCot.jsx";

/**
 * Danh mục công cụ.
 * Đầu trang cùng kiểu các mục khác (Playbook, Nguồn vốn): lời giới thiệu bên
 * trái, khung xanh vẽ từ dữ liệu bên phải; chạm một trạm trong khung là lọc.
 * Bên dưới là lưới thẻ gọn: biểu tượng, tên, một câu hỏi ngắn, số phút.
 *
 * Thứ tự trạm và công cụ lấy từ lo-trinh.js (congCu), nên sửa lộ trình là
 * danh mục tự đổi theo. Lọc trạm nằm trên URL (?tram=) để chia sẻ được.
 */

const KHAC = "khac";

function TheCongCu({ t, tram, mau, chinh }) {
  return (
    <Link className="cct" to={"/cong-cu/" + t.slug} style={{ "--mau": mau }}>
      <span className="cct-dau">
        <span className="cct-icon"><CongCuIcon slug={t.slug} /></span>
        {tram && <span className="cct-tram">{tram.laXuatPhat ? "Bắt đầu" : `Trạm ${tram.so}`}</span>}
      </span>
      <span className="cct-ten">{t.title}</span>
      <span className="cct-hoi">{CAU_HOI_CONG_CU[t.slug] || t.blurb}</span>
      <span className="cct-chan">
        <span>{t.minutes} phút</span>
        {chinh && <span className="cct-nen">Nên làm trước</span>}
        <span className="cct-dung">Dùng ngay →</span>
      </span>
    </Link>
  );
}

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const luoi = useRef(null);
  const q = params.get("q") || "";
  const tramChon = docChon(params, "tram"),
    stageChon = docChon(params, "stage");
  const change = (key, value) => {
    const p = new URLSearchParams(params);
    value ? p.set(key, value) : p.delete(key);
    setParams(p, { replace: true });
  };
  const doiNhom = (key) => (ds) => setParams(ghiChon(params, key, ds), { replace: true });

  const sanSang = TOOLS.filter((t) => t.status === "READY");
  const theoSlug = new Map(sanSang.map((t) => [t.slug, t]));
  const hopChu = (x) =>
    normalize(x.t.title + " " + x.t.blurb + " " + (CAU_HOI_CONG_CU[x.t.slug] || "")).includes(normalize(q));

  // Nhóm theo trạm (cho banner) và danh sách phẳng theo thứ tự lộ trình (cho lưới).
  const daXep = new Set();
  const nhom = TRAM.map((tram, i) => {
    const ds = tram.congCu.map((s) => theoSlug.get(s)).filter(Boolean);
    ds.forEach((t) => daXep.add(t.slug));
    return { id: tram.id, so: tram.so, ten: tram.laXuatPhat ? "Điểm xuất phát" : tram.ten, tram, ds, mau: MAU_TRAM[i % MAU_TRAM.length] };
  });
  // Công cụ chưa gắn trạm vẫn phải hiện, để không ai mất công cụ vì quên sửa lộ trình.
  const ngoai = sanSang.filter((t) => !daXep.has(t.slug));
  if (ngoai.length) nhom.push({ id: KHAC, so: 0, ten: "Khác", tram: null, ds: ngoai, mau: "#a9bccd" });

  const tatCaThe = nhom.flatMap((n) =>
    n.ds.map((t) => ({ t, n, chinh: n.tram && t.slug === n.tram.congCuChinh && n.tram.congCu.length > 1 })),
  );
  const { ketQua: cacThe, dem } = locVaDem(
    tatCaThe,
    [
      { key: "tram", giaTri: (x) => [x.n.id] },
      { key: "stage", giaTri: (x) => x.t.stages || [] },
    ],
    { tram: tramChon, stage: stageChon },
    hopChu,
  );
  const dangLoc = Boolean(q || tramChon.length || stageChon.length);
  const tramMot = tramChon.length === 1 ? nhom.find((n) => n.id === tramChon[0]) : null;
  const xuatPhat = nhom.find((n) => n.tram?.laXuatPhat)?.ds[0];
  const soTram = nhom.filter((n) => n.so).length;

  const chonTram = (id) => {
    change("tram", id);
    if (id) requestAnimationFrame(() => luoi.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  return (
    <>
      <div className="cc-dau">
        <header>
          <p className="eyebrow">CÔNG CỤ</p>
          <h1>Bộ công cụ gọi vốn</h1>
          <p className="lead">Mỗi công cụ trả lời một câu hỏi nhà đầu tư sẽ hỏi. Nhập vài con số, có kết quả ngay.</p>
          <div className="cc-dau-nut">
            {xuatPhat && (
              <Link className="nut" to={"/cong-cu/" + xuatPhat.slug}>Bắt đầu với {xuatPhat.title} →</Link>
            )}
            <Link className="cc-dau-link" to="/lo-trinh">Lộ trình {soTram} trạm →</Link>
          </div>
        </header>
        <CongCuBanDo nhom={nhom} dangChon={tramChon.length === 1 ? tramChon[0] : ""} onChon={chonTram} />
      </div>

      <div ref={luoi}>
        <KhungHaiCot
          soDangChon={tramChon.length + stageChon.length}
          boLoc={<>
            <NhomLoc
              tieuDe="Trạm"
              chon={tramChon}
              onDoi={doiNhom("tram")}
              ds={nhom.map((n) => ({
                v: n.id,
                nhan: n.tram?.laXuatPhat ? "Bắt đầu" : n.so ? `Trạm ${n.so}: ${n.ten}` : n.ten,
                dem: dem.tram[n.id] || 0,
                mau: n.mau,
              }))}
            />
            <NhomLoc
              tieuDe="Giai đoạn của bạn"
              chon={stageChon}
              onDoi={doiNhom("stage")}
              ds={Object.entries(STAGE_LABELS).map(([k, v]) => ({ v: k, nhan: v, dem: dem.stage[k] || 0 }))}
            />
          </>}
        >
          <OTim id="tool-search" nhan="Tìm công cụ" value={q} onChange={(v) => change("q", v)} placeholder="Tìm: dòng tiền, cổ phần, định giá…" />
          <DongDem dangLoc={dangLoc} onXoa={() => setParams({}, { replace: true })}>
            {tramMot?.tram?.cauHoi
              ? <><b>{cacThe.length} công cụ</b>: {tramMot.tram.cauHoi}</>
              : <>Hiển thị <b>{cacThe.length}</b> trên {tatCaThe.length} công cụ</>}
          </DongDem>
          {cacThe.length > 0 ? (
            <div className="cc-luoi">
              {cacThe.map(({ t, n, chinh }) => (
                <TheCongCu key={t.toolId} t={t} tram={n.tram} mau={n.mau} chinh={chinh} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <h2>Chưa có công cụ cho lựa chọn này</h2>
              <p>Thử từ khóa ngắn hơn hoặc bỏ bớt bộ lọc.</p>
              <button className="nut phu" onClick={() => setParams({})}>
                Xóa bộ lọc
              </button>
            </div>
          )}
        </KhungHaiCot>
      </div>
    </>
  );
}
